// singular.service.ts
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { SingularSdkInterface } from './interface/singular-sdk.interface';
import { CordovaSingularSdk } from './implementations/cordova-singular.service';
import { WebSingularSdk } from './implementations/web-singular.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { HEADER_DONT_SAVE } from '@app/constants';
import { Observable, of } from 'rxjs';
declare const cordova: any;

@Injectable({
  providedIn: 'root',
})
export class SingularService {
  private readonly SINGULAR_API_KEY = 'monific_2423dd5f';
  private readonly SINGULAR_SECRET_KEY = '7f0e4f9ee48138b2d6b0e825a140cf30';
  private readonly APP_PACKAGE_NAME = 'com.monific.app';
  private readonly PERSISTENT_ID = 'monific.com';

  private sdk: SingularSdkInterface;
  sdkType: 'cordova' | 'web' | 'none' = 'none';
  isNative = Capacitor?.isNative || Capacitor.isNativePlatform();
  capacitorPlatform = Capacitor?.platform || Capacitor.getPlatform();
  cordovaExist = false;
  cordovaPlugins: any;
  userAgent = navigator.userAgent;

  constructor(
   private readonly httpClient: HttpClient,
  ) {

   try {
      this.cordovaExist = typeof cordova !== 'undefined';
      if (this.cordovaExist && cordova.plugins) {
         this.cordovaPlugins = JSON.stringify(cordova.plugins);
         console.info('Se ha cargado el SDK Cordova de Singular.');
      }
   } catch (error) {
      this.cordovaExist = false;
      this.cordovaPlugins = null;
   }

   this.isNative = Capacitor?.isNative || Capacitor.isNativePlatform();

    if (this.isNative || this.cordovaExist) {
      try {
        this.sdk = new CordovaSingularSdk(
          this.SINGULAR_API_KEY,
          this.SINGULAR_SECRET_KEY,
          this.APP_PACKAGE_NAME,
          this.PERSISTENT_ID
        );
        this.sdk.init();
        this.sdkType = 'cordova';
        console.info('Se ha cargado el SDK Cordova de Singular.');
      } catch (error) {
        console.log(
          'Fallo al cargar el SDK Cordova. Intentando cargar el SDK web.',
          error
        );
        this.initializeWebSdk();
      }
    } else {
      this.initializeWebSdk();
    }
  }

  private registerEventInternal(data: {
   eventName: string, project?: string, amount?: string, total?: string,
   userAgent: string, native: boolean, sdk: string, deviceId?: string,
  }) {
   return this.httpClient.post<{ info: any }>(`${environment.apiUrlBase}/user/register-event`, data, HEADER_DONT_SAVE)
  }

  private initializeWebSdk(): void {
    try {
      this.sdk = new WebSingularSdk(
        this.SINGULAR_API_KEY,
        this.SINGULAR_SECRET_KEY,
        this.APP_PACKAGE_NAME,
        this.PERSISTENT_ID
      );
      this.sdk.init();
      this.sdkType = 'web';
    } catch (error) {
      this.sdkType = 'none';
      console.log('Fallo al cargar el SDK web de Singular:', error);
    }
  }

  /**
   * Registra una visita a una página.
   */
  public pageVisit(): void {
    if (this.sdkType === 'none') return;
    try {
      this.sdk.pageVisit();
      console.info('Visita a página registrada.');
    } catch (error) {
      console.log('Error al registrar visita a página:', error);
      this.handleSdkFailure();
    }
  }

  /**
   * Obtiene el ID de dispositivo predeterminado de Singular.
   * @returns ID de dispositivo o cadena vacía si no está disponible.
   */
  public getDeviceDefaultID(): string {
    if (this.sdkType === 'none') return '';
    try {
      const deviceId = this.sdk.getDeviceDefaultID();
      return deviceId;
    } catch (error) {
      console.log('Error al obtener el ID de dispositivo Singular:', error);
      this.handleSdkFailure();
      return '';
    }
  }

  /**
   * Obtiene el ID de dispositivo específico del navegador.
   * @returns ID de dispositivo o 'NA' si no está disponible.
   */
  public browserDeviceID(): string {
    if (this.sdkType === 'none') return 'NA';
    try {
      const deviceId = this.sdk.browserDeviceID();
      console.info('ID de dispositivo del navegador:', deviceId);
      return deviceId;
    } catch (error) {
      console.log(
        'Error al obtener el ID de dispositivo del navegador:',
        error
      );
      this.handleSdkFailure();
      return 'NA';
    }
  }

  /**
   * Registra el inicio de sesión de un usuario.
   * @param cognitoUser Nombre de usuario de Cognito.
   */
  public login(cognitoUser: string): void {
    if (this.sdkType === 'none') return;
    try {
      this.sdk.login(cognitoUser);
    } catch (error) {
      console.log(
        `Error al iniciar sesión para el usuario ${cognitoUser}:`,
        error
      );
      this.handleSdkFailure();
    }
  }

  /**
   * Registra el cierre de sesión de un usuario.
   */
  public logout(): void {
    if (this.sdkType === 'none') return;
    try {
      this.sdk.logout();
      // console.info('Usuario cerró sesión.');
    } catch (error) {
      console.log('Error al cerrar sesión del usuario:', error);
      this.handleSdkFailure();
    }
  }

  /**
   * Envía un evento personalizado a Singular.
   * @param eventName Nombre del evento.
   * @param args Argumentos adicionales del evento.
   */
  public async sendEvent(eventName: string, args?: any) {
    if (this.sdkType === 'none') return;
    try {
      await this.sdk.sendEvent(eventName, args);
    } catch (error) {
      console.log(`Error al enviar el evento ${eventName}:`, error);
      this.handleSdkFailure();
    }
  }

  registerInternalEvent(eventName: string, args?: any):Observable<any> {
    if (args.project && args.amount) {
      return this.registerEventInternal({
        eventName,
        project: args.project,
        amount: args.amount,
        total: args.total,
        userAgent: this.userAgent,
        native: this.isNative,
        sdk: this.sdkType,
        deviceId: this.getDeviceDefaultID() || this.browserDeviceID() || 'NA',
      })
    }
    return of(true)
  }

  /**
   * Registra una transacción de ingresos.
   * @param itemName Nombre del artículo.
   * @param currency Moneda utilizada.
   * @param amount Monto de la transacción.
   * @param args Argumentos adicionales de la transacción.
   */
  public revenue(
    itemName: string,
    currency: string,
    amount: number,
    args?: any
  ): void {
    if (this.sdkType === 'none') return;
    try {
      this.sdk.revenue(itemName, currency, amount, args);
      // console.info(
      //   `Ingresos registrados para ${itemName}: ${currency} ${amount}`,
      //   args
      // );
    } catch (error) {
      console.log(`Error al registrar ingresos para ${itemName}:`, error);
      this.handleSdkFailure();
    }
  }

  /**
   * Maneja fallos en el SDK actual y cambia a la alternativa disponible.
   */
  private handleSdkFailure(): void {
    if (this.sdkType === 'cordova') {
      console.log(
        'Intentando cambiar al SDK web de Singular debido a un fallo en Cordova.'
      );
      this.initializeWebSdk();
    } else {
      console.error('No hay SDK disponible para manejar la operación.');
      this.sdkType = 'none';
    }
  }
}
